// wsSimulator: drives mock campaigns forward on a timer. Moves queued points
// to running, running to done, then after a full batch completes emits a
// metrics rollup and asks the mock sampler for the next batch.

import type {
  CampaignState,
  SamplePoint,
  WebSocketEvent,
} from "../types";
import { campaignStore } from "./campaignStore";
import { lookup as oracleLookup } from "./oracleLoader";
import {
  fitAllOutputs,
  makeRng,
  normalisePoint,
  proposeBatch,
  rangeAxes,
  type PerOutputModel,
} from "./_mock_sampler";
import { predict } from "./surrogate";
import { proposeRealTailorBatch } from "../realTailorBridge";

const TICK_MS = 500; // 2 Hz

type PointSimState = {
  startMs: number;
  targetDurationSec: number;
};

type CampaignRuntime = {
  pointSim: Map<string, PointSimState>; // pointId -> sim state
  nextBatchArmed: boolean;
  seedPart: number;
};
type SamplerProposal = {
  inputs: Record<string, number>;
  decision?: SamplePoint["samplerDecision"];
  truthOutputs?: Record<string, number>;
  predicted?: Record<string, { mean: number; sigma: number }>;
};

const RUNTIMES = new Map<string, CampaignRuntime>();

function getOrCreateRuntime(id: string): CampaignRuntime {
  let rt = RUNTIMES.get(id);
  if (!rt) {
    rt = { pointSim: new Map(), nextBatchArmed: false, seedPart: 0 };
    RUNTIMES.set(id, rt);
  }
  return rt;
}

let tickTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleTick(): void {
  if (tickTimer) return;
  tickTimer = setTimeout(() => {
    tickTimer = null;
    void tickAll().finally(() => {
      if (campaignStore.all().some((state) => state.status === "running")) scheduleTick();
    });
  }, TICK_MS);
}

export function ensureTickLoopRunning(): void { scheduleTick(); }
export function stopTickLoop(): void { if (tickTimer) clearTimeout(tickTimer); tickTimer = null; }

function now(): number { return Date.now(); }

function makeRuntimeRng(campaignId: string, pointId: string): () => number {
  return makeRng(campaignId, `runtime-${pointId}`);
}

function simulateRuntimeSec(campaignId: string, pointId: string): number {
  return 10 + 30 * makeRuntimeRng(campaignId, pointId)();
}

function truthForPoint(
  state: CampaignState,
  point: SamplePoint,
): Record<string, number> | null {
  if (point.truthOutputs) return point.truthOutputs;
  if (state.config.sampler?.engine === "tailor-mannequin" || state.config.sampler?.engine === "lhs-mannequin") {
    // Real Mannequin campaigns arrive with Python-oracle values attached to
    // each proposal. Never fall back to the old TypeScript oracle here.
    return null;
  }
  const oracle = state.config.oracle;
  if (!oracle?.enabled || !oracle.datasetId) return null;
  const res = oracleLookup(oracle.datasetId, point.inputs);
  if (!res.ok) return null;
  const filtered: Record<string, number> = {};
  for (const o of state.config.outputs) {
    const v = res.outputs[o.name];
    if (v != null) filtered[o.name] = v;
  }
  return filtered;
}

async function proposeSamplerBatch(
  state: CampaignState,
  observed: SamplePoint[],
  batchSize: number,
  iteration: number,
): Promise<SamplerProposal[]> {
  if (state.config.sampler?.engine === "tailor-mannequin" || state.config.sampler?.engine === "lhs-mannequin") {
    return proposeRealTailorBatch(state, observed, batchSize, iteration);
  }
  return proposeBatch(
    state.id,
    state.config.inputs,
    state.config.outputs,
    observed,
    batchSize,
    iteration,
    state.config.postDivergenceSampling,
    state.config.sampler?.symmetries ?? state.config.sampler?.symmetry,
  ).map((inputs) => ({
    inputs,
    decision: {
      engine: "mock-surrogate",
      label: "Mock surrogate",
      selectedBy: "mock_score",
      reason: "Mock surrogate score",
    },
  }));
}

function syntheticOutputs(
  state: CampaignState,
  point: SamplePoint,
  models: PerOutputModel[],
  rng: () => number,
): Record<string, number> {
  const range = rangeAxes(state.config.inputs);
  const xn = normalisePoint(point.inputs, range);
  const out: Record<string, number> = {};
  for (const { name, model } of models) {
    const { mean, sigma } = predict(model, xn);
    // Fallback when no data: small signal from input magnitude.
    const base = Number.isFinite(mean) ? mean : 0;
    const noise = (rng() - 0.5) * 2 * Math.max(sigma, 0.05);
    out[name] = base + noise;
  }
  return out;
}

function completedObservations(state: CampaignState): SamplePoint[] {
  return state.points.filter(
    (p) => p.status === "done" && p.outputs != null,
  );
}

function currentBatchComplete(state: CampaignState): boolean {
  const activeBatchId = state.points
    .filter((p) => p.iter === state.iteration)
    .map((p) => p.batchId)[0];
  if (!activeBatchId) return false;
  const batch = state.points.filter((p) => p.batchId === activeBatchId);
  if (batch.length === 0) return false;
  return batch.every((p) => p.status === "done" || p.status === "failed");
}

function targetsMet(state: CampaignState): boolean {
  if (!state.config.stopEarlyIfTargetsMet) return false;
  let anyChecked = false;
  for (const o of state.config.outputs) {
    const m = state.metrics.perOutput[o.name];
    if (!m || o.targetSigma == null) continue;
    anyChecked = true;
    if (m.currentMaxSigma > o.targetSigma) return false;
  }
  return anyChecked;
}

function recomputeMetrics(state: CampaignState): void {
  const observed = completedObservations(state);
  if (isMannequinDemoState(state)) {
    for (const output of state.config.outputs) {
      const metric = state.metrics.perOutput[output.name];
      if (!metric || observed.length === 0) continue;
      const predictions = observed
        .map((point) => point.predicted?.[output.name])
        .filter((prediction): prediction is { mean: number; sigma: number } => prediction != null);
      if (predictions.length) {
        metric.currentMaxSigma = Math.max(...predictions.map((prediction) => prediction.sigma));
        metric.currentMeanSigma = predictions.reduce((sum, prediction) => sum + prediction.sigma, 0) / predictions.length;
      }
      const errors = observed.flatMap((point) => {
        const prediction = point.predicted?.[output.name];
        const truth = point.truthOutputs?.[output.name];
        return prediction && truth != null ? [prediction.mean - truth] : [];
      });
      if (errors.length) {
        metric.currentRealRmse = Math.sqrt(errors.reduce((sum, error) => sum + error * error, 0) / errors.length);
      }
    }
    return;
  }
  const models = fitAllOutputs(state.config.inputs, state.config.outputs, observed);
  const range = rangeAxes(state.config.inputs);

  // Sample sigma at the current observed points to get "currentMax/Mean".
  for (const o of state.config.outputs) {
    const m = state.metrics.perOutput[o.name];
    if (!m) continue;
    const model = models.find((x) => x.name === o.name)?.model;
    if (!model || observed.length === 0) continue;
    let maxSig = 0;
    let sumSig = 0;
    for (const p of observed) {
      const xn = normalisePoint(p.inputs, range);
      const pred = predict(model, xn);
      const { sigma } = pred;
      if (sigma > maxSig) maxSig = sigma;
      sumSig += sigma;
    }
    m.currentMaxSigma = maxSig;
    m.currentMeanSigma = observed.length > 0 ? sumSig / observed.length : 0;
  }

  if (state.config.oracle?.enabled && state.config.oracle.datasetId && range.length >= 2) {
    const ax1 = range[0];
    const ax2 = range[1];
    if (!ax1 || !ax2) return;
    const datasetId = state.config.oracle.datasetId;
    const res = 50;
    const xs: number[] = [];
    const ys: number[] = [];
    const xMin = ax1.min ?? 0;
    const xMax = ax1.max ?? 1;
    const yMin = ax2.min ?? 0;
    const yMax = ax2.max ?? 1;
    for (let i = 0; i < res; i++) xs.push(xMin + ((xMax - xMin) * i) / (res - 1));
    for (let j = 0; j < res; j++) ys.push(yMin + ((yMax - yMin) * j) / (res - 1));

    for (const o of state.config.outputs) {
      const m = state.metrics.perOutput[o.name];
      if (!m || !m.realRmseByIter) continue;
      const model = models.find((x) => x.name === o.name)?.model;
      if (!model) continue;

      let sse = 0;
      let cnt = 0;
      for (const xv of xs) {
        for (const yv of ys) {
          const inputs: Record<string, number> = { [ax1.name]: xv, [ax2.name]: yv };
          for (const a of range.slice(2)) {
            inputs[a.name] = ((a.min ?? 0) + (a.max ?? 1)) / 2;
          }
          for (const a of state.config.inputs) {
            if (a.mode === "fixed" && a.fixedValue != null) {
              inputs[a.name] = a.fixedValue;
            }
          }
          const truth = oracleLookup(datasetId, inputs);
          if (!truth.ok) continue;
          const t = truth.outputs[o.name];
          if (t == null) continue;
          const xn = normalisePoint(inputs, range);
          const { mean } = predict(model, xn);
          const err = mean - t;
          sse += err * err;
          cnt += 1;
        }
      }
      if (cnt > 0) {
        const rmse = Math.sqrt(sse / cnt);
        m.currentRealRmse = rmse;
      }
    }
  }
}

function pushBatchMetrics(state: CampaignState): void {
  for (const o of state.config.outputs) {
    const m = state.metrics.perOutput[o.name];
    if (!m) continue;
    m.maxSigmaByIter.push(m.currentMaxSigma);
    m.meanSigmaByIter.push(m.currentMeanSigma);
    if (m.realRmseByIter && m.currentRealRmse != null) {
      m.realRmseByIter.push(m.currentRealRmse);
    }
  }
}

function recordCalibration(state: CampaignState, point: SamplePoint): void {
  if (!state.config.oracle?.enabled) return;
  if (!point.outputs || !point.predicted || !point.truthOutputs) return;
  for (const o of state.config.outputs) {
    const m = state.metrics.perOutput[o.name];
    if (!m) continue;
    const predicted = point.predicted[o.name];
    const truth = point.truthOutputs[o.name];
    if (predicted == null || truth == null) continue;
    if (!m.calibration) m.calibration = [];
    m.calibration.push({
      predictedSigma: predicted.sigma,
      realisedAbsError: Math.abs((point.outputs[o.name] ?? 0) - truth),
    });
  }
}

function isMannequinDemoState(state: CampaignState): boolean {
  return state.config.sampler?.engine === "tailor-mannequin" || state.config.sampler?.engine === "lhs-mannequin";
}

function completePointNow(
  state: CampaignState,
  point: SamplePoint,
  models: PerOutputModel[],
  range: ReturnType<typeof rangeAxes>,
  runtimeRng: () => number,
): void {
  if (range.length > 0) {
    const xn = normalisePoint(point.inputs, range);
    const pred: Record<string, { mean: number; sigma: number }> = {};
    if (point.predicted) {
      Object.assign(pred, point.predicted);
    } else if (isMannequinDemoState(state)) {
      throw new Error("Real Tailor proposal is missing its Python posterior prediction.");
    } else {
      for (const { name, model } of models) pred[name] = predict(model, xn);
    }
    point.predicted = pred;
  }

  point.status = "done";
  point.runtimeSec = simulateRuntimeSec(state.id, point.id);
  const perRun = state.config.flexCreditsPerHighFiRun ?? 1;
  point.costFlexCredits = Math.max(
    1,
    Math.min(2 * perRun, (point.runtimeSec * perRun) / 30),
  );

  const truth = truthForPoint(state, point);
  if (truth) {
    point.outputs = isMannequinDemoState(state) ? { ...truth } : Object.fromEntries(
      Object.entries(truth).map(([key, value]) => {
        const noiseRng = makeRng(state.id, `noise-${point.id}-${key}`);
        return [key, value + (noiseRng() - 0.5) * 0.02 * (Math.abs(value) + 0.1)];
      }),
    );
    point.truthOutputs = truth;
  } else if (isMannequinDemoState(state)) {
    throw new Error("Real Tailor proposal is missing its Python Mannequin oracle value.");
  } else {
    point.outputs = syntheticOutputs(state, point, models, runtimeRng);
  }

  if (point.source === "manual") state.metrics.manualRunCount += 1;
  else state.metrics.autonomousRunCount += 1;
  state.metrics.cumulativeCostFlexCredits += point.costFlexCredits ?? 0;
  recordCalibration(state, point);
}

function completeBatchNow(
  state: CampaignState,
  points: SamplePoint[],
  rt: CampaignRuntime,
): WebSocketEvent[] {
  const completed = completedObservations(state);
  // Mannequin proposals already carry Python Tailor predictions and oracle
  // responses. Refitting the legacy JS dense surrogate here is redundant and
  // becomes a catastrophic client-side O(N^3) pause for streaming campaigns.
  const models = isMannequinDemoState(state)
    ? []
    : fitAllOutputs(state.config.inputs, state.config.outputs, completed);
  const range = rangeAxes(state.config.inputs);
  const runtimeRng = makeRng(state.id, `instant-tranche-${state.iteration}`);
  const events: WebSocketEvent[] = [];
  for (const point of points) {
    completePointNow(state, point, models, range, runtimeRng);
    rt.pointSim.delete(point.id);
    events.push({ type: "point", campaignId: state.id, point: { ...point } });
  }
  state.pointsComplete = state.points.filter((point) => point.status === "done").length;
  state.pointsInFlight = state.points.filter((point) => point.status === "queued" || point.status === "running").length;
  recomputeMetrics(state);
  pushBatchMetrics(state);
  state.status = "done";
  events.push({ type: "metrics", campaignId: state.id, metrics: { ...state.metrics } });
  events.push({ type: "completed", campaignId: state.id });
  return events;
}

async function tickAll(): Promise<void> {
  await Promise.all(campaignStore.all()
    .filter((state) => state.status === "running")
    .map((state) => tickCampaign(state)));
}

async function tickCampaign(state: CampaignState): Promise<void> {
  const rt = getOrCreateRuntime(state.id);
  const runtimeRng = makeRng(state.id, `tick-${rt.seedPart++}`);

  const completed = completedObservations(state);
  const models = fitAllOutputs(
    state.config.inputs,
    state.config.outputs,
    completed,
  );
  const range = rangeAxes(state.config.inputs);
  const events: WebSocketEvent[] = [];

  // Move queued points to running; initialise their sim timers.
  for (const p of state.points) {
    if (p.status === "queued") {
      p.status = "running";
      const targetSec = simulateRuntimeSec(state.id, p.id);
      rt.pointSim.set(p.id, { startMs: now(), targetDurationSec: targetSec });
      // Record surrogate prediction at the point so we have predicted sigma.
      if (range.length > 0) {
        const xn = normalisePoint(p.inputs, range);
        const pred: Record<string, { mean: number; sigma: number }> = {};
        if (p.predicted) {
          Object.assign(pred, p.predicted);
        } else if (isMannequinDemoState(state)) {
          throw new Error("Real Tailor proposal is missing its Python posterior prediction.");
        } else {
          for (const { name, model } of models) {
            pred[name] = predict(model, xn);
          }
        }
        p.predicted = pred;
      }
      events.push({ type: "point", campaignId: state.id, point: { ...p } });
    }
  }

  // Advance running points. Complete those whose sim time has elapsed.
  for (const p of state.points) {
    if (p.status !== "running") continue;
    const sim = rt.pointSim.get(p.id);
    if (!sim) continue;
    const elapsedSec = (now() - sim.startMs) / 1000;
    // Scale sim time so mock campaigns complete quickly: compress 10-40 sim
    // seconds into ~1-4 wall seconds by default. Fast mode (used by the
    // Playwright acceptance suite) further compresses to ~0.1-0.4 wall s.
    const speed =
      import.meta.env.VITE_ADAPTIVE_SAMPLING_FAST === "true" ? 100 : 10;
    if (elapsedSec * speed >= sim.targetDurationSec) {
      p.status = "done";
      p.runtimeSec = sim.targetDurationSec;
      const perRun = state.config.flexCreditsPerHighFiRun ?? 1;
      p.costFlexCredits = Math.max(
        1,
        Math.min(2 * perRun, (sim.targetDurationSec * perRun) / 30),
      );

      // Outputs: oracle-derived if available, else synthetic from surrogate.
      const truth = truthForPoint(state, p);
      if (truth) {
        p.outputs = isMannequinDemoState(state) ? { ...truth } : Object.fromEntries(
          Object.entries(truth).map(([key, value]) => {
            const noiseRng = makeRng(state.id, `noise-${p.id}-${key}`);
            return [key, value + (noiseRng() - 0.5) * 0.02 * (Math.abs(value) + 0.1)];
          }),
        );
        p.truthOutputs = truth;
      } else if (isMannequinDemoState(state)) {
        throw new Error("Real Tailor proposal is missing its Python Mannequin oracle value.");
      } else {
        p.outputs = syntheticOutputs(state, p, models, runtimeRng);
      }

      // Update autonomous/manual counters and cost.
      if (p.source === "manual") {
        state.metrics.manualRunCount += 1;
      } else {
        state.metrics.autonomousRunCount += 1;
      }
      state.metrics.cumulativeCostFlexCredits += p.costFlexCredits ?? 0;

      recordCalibration(state, p);
      rt.pointSim.delete(p.id);
      events.push({ type: "point", campaignId: state.id, point: { ...p } });
    }
  }

  // Recount in-flight / complete.
  state.pointsComplete = state.points.filter((p) => p.status === "done").length;
  state.pointsInFlight = state.points.filter(
    (p) => p.status === "queued" || p.status === "running",
  ).length;

  // If the current batch is complete, publish metrics and propose next batch.
  if (currentBatchComplete(state) && state.points.length > 0) {
    recomputeMetrics(state);
    pushBatchMetrics(state);
    events.push({ type: "metrics", campaignId: state.id, metrics: { ...state.metrics } });

    const budgetUsed = state.points.filter((p) => p.source === "sampler").length;
    const budget = state.config.budget ?? Infinity;
    const shouldStop = budgetUsed >= budget || targetsMet(state);

    if (shouldStop) {
      state.status = "done";
      events.push({ type: "completed", campaignId: state.id });
    } else if (!rt.nextBatchArmed) {
      rt.nextBatchArmed = true;
      const proposals = await proposeSamplerBatch(
        state,
        completedObservations(state),
        state.config.batchSize,
        state.iteration + 1,
      );
      if (proposals.length > 0) {
        state.iteration += 1;
        const batchId = campaignStore.nextBatchId("sampler");
        const newIds: string[] = [];
        for (const proposal of proposals) {
          const pid = campaignStore.nextPointId();
          state.points.push({
            id: pid,
            iter: state.iteration,
            batchId,
            status: "queued",
            source: "sampler",
            inputs: proposal.inputs,
            samplerDecision: proposal.decision,
            truthOutputs: proposal.truthOutputs,
            predicted: proposal.predicted,
          });
          newIds.push(pid);
        }
        events.push({
          type: "batch",
          campaignId: state.id,
          iteration: state.iteration,
          pointIds: newIds,
        });
      }
      rt.nextBatchArmed = false;
    }
  }

  campaignStore.set(state);
  for (const ev of events) campaignStore.emit(state.id, ev);
}

// Used by the router after creating a campaign / resuming from paused.
export async function kickCampaign(state: CampaignState): Promise<void> {
  ensureTickLoopRunning();
  getOrCreateRuntime(state.id);
  // If no points yet, seed with an initial sampler batch.
  if (state.points.length === 0) {
    const proposals = await proposeSamplerBatch(state, [], state.config.batchSize, 1);
    if (proposals.length > 0) {
      state.iteration = 1;
      const batchId = campaignStore.nextBatchId("sampler");
      const newIds: string[] = [];
      for (const proposal of proposals) {
        const pid = campaignStore.nextPointId();
        state.points.push({
          id: pid,
          iter: 1,
          batchId,
          status: "queued",
          source: "sampler",
          inputs: proposal.inputs,
          samplerDecision: proposal.decision,
          truthOutputs: proposal.truthOutputs,
          predicted: proposal.predicted,
        });
        newIds.push(pid);
      }
      state.pointsInFlight = state.points.length;
      campaignStore.set(state);
      campaignStore.emit(state.id, { type: "state", campaignId: state.id, state });
      campaignStore.emit(state.id, {
        type: "batch",
        campaignId: state.id,
        iteration: 1,
        pointIds: newIds,
      });
    }
  }
}

export async function addSamplerTranche(state: CampaignState, additionalBudget = 0): Promise<string[]> {
  ensureTickLoopRunning();
  const rt = getOrCreateRuntime(state.id);
  const open = state.points.filter((p) => p.status === "queued" || p.status === "running");
  if (open.length > 0) {
    state.status = "running";
    state.pointsInFlight = open.length;
    campaignStore.set(state);
    campaignStore.emit(state.id, { type: "state", campaignId: state.id, state });
    return open.map((point) => point.id);
  }
  const requestedBatchSize = additionalBudget > 0 ? Math.floor(additionalBudget) : state.config.batchSize;
  const samplerPointCount = state.points.filter((point) => point.source === "sampler").length;
  // A tranche proposal is an external operation and can fail. Keep the mock
  // campaign update transactional: publishing the larger budget before the
  // Python Tailor request resolves leaves a campaign that says (for example)
  // 112/112 while it only contains 104 samples. The headless auto-run then
  // quite reasonably believes there is nothing left to request.
  //
  // `reservedBudget` also lets a page recover a campaign produced by an older
  // hot-reloaded client with exactly that torn state. The next request fills
  // the already-reserved gap before extending the budget again.
  const reservedBudget = Math.max(samplerPointCount, state.config.budget);
  const reservedGap = Math.max(0, reservedBudget - samplerPointCount);
  const budgetIncrement = Math.max(0, requestedBatchSize - reservedGap);
  const proposals = await proposeSamplerBatch(
    state,
    completedObservations(state),
    requestedBatchSize,
    state.iteration + 1,
  );
  if (proposals.length === 0) {
    // Empty proposals are retryable and must not consume budget either.
    campaignStore.set(state);
    campaignStore.emit(state.id, { type: "state", campaignId: state.id, state });
    return [];
  }
  if (budgetIncrement > 0) state.config.budget += budgetIncrement;
  state.status = "running";
  state.iteration += 1;
  const batchId = campaignStore.nextBatchId("sampler");
  const pointIds: string[] = [];
  const newPoints: SamplePoint[] = [];
  for (const proposal of proposals) {
    const pid = campaignStore.nextPointId();
    const point: SamplePoint = {
      id: pid,
      iter: state.iteration,
      batchId,
      status: "queued",
      source: "sampler",
      inputs: proposal.inputs,
      samplerDecision: proposal.decision,
      truthOutputs: proposal.truthOutputs,
      predicted: proposal.predicted,
    };
    state.points.push(point);
    newPoints.push(point);
    pointIds.push(pid);
  }
  state.pointsInFlight = state.points.filter((p) => p.status === "queued" || p.status === "running").length;
  const immediateEvents = isMannequinDemoState(state) ? completeBatchNow(state, newPoints, rt) : [];
  campaignStore.set(state);
  campaignStore.emit(state.id, { type: "state", campaignId: state.id, state });
  campaignStore.emit(state.id, {
    type: "batch",
    campaignId: state.id,
    iteration: state.iteration,
    pointIds,
  });
  for (const event of immediateEvents) campaignStore.emit(state.id, event);
  return pointIds;
}

export function subscribeMockStream(
  campaignId: string,
  onEvent: (ev: WebSocketEvent) => void,
): () => void {
  ensureTickLoopRunning();
  return campaignStore.subscribe(campaignId, onEvent);
}

// Exported for router use: public helpers for surface/score grids.
export { fitAllOutputs, recomputeMetrics };
